// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * RX-DRAWER-1: right-click row drawer for the prescription queues.
 *
 * The 15s list refresh re-sorts by last activity, so the row a user is working
 * on slides away under their hands. The drawer pins the WORKING CONTEXT by Rx
 * id instead of by grid position: it renders from the already-loaded row data
 * (zero extra API calls to a 1-vCore DB), stays open across list refreshes,
 * and carries its state in the URL (?rxd=<id>) so a reload / deep link / the
 * NEXT-1 login-return flow all restore it.
 *
 * Entry: right-click a row (contextmenu is preventDefault'ed on rows only).
 * A dismissible one-line hint above the table teaches the shortcut.
 * Left-click behavior is untouched (navigates to the full Rx page, as always).
 */
import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { X, ExternalLink, Lightbulb } from 'lucide-react'
import { WorkBenchCell, WorkBenchDateCell, type useWorkBenchDefs } from './WorkBench'

export interface DrawerField { label: string; node: React.ReactNode }
export interface DrawerWbField { key: string; label: string; current: string | null }

const rowStyle: React.CSSProperties = {
  display: 'flex', gap: 10, padding: '7px 0',
  borderBottom: '1px solid var(--color-border, #eee)', fontSize: 13, alignItems: 'baseline',
}
const labStyle: React.CSSProperties = {
  flex: '0 0 140px', color: 'var(--color-text-mute, #6b7280)', fontSize: 12,
}

export function RxRowDrawer({
  title, subtitle, fields, wbFields, wbDefs, wbRxId, openHref, onClose, notFound, onWbSaved,
}: {
  /** Header line: e.g. Rx number + patient. */
  title: string
  subtitle?: string
  /** Read-only display fields, pre-rendered by the owning grid. */
  fields: DrawerField[]
  /** Editable workbench fields (rendered as the same inline cells the grid uses). */
  wbFields: DrawerWbField[]
  wbDefs: ReturnType<typeof useWorkBenchDefs>
  /** The id workbench PUTs go to: the ROW'S OWN id, which in a fill-grain row is the fill id.
   *  This previously demanded the prescription id, on the belief that the PUT 404s on a fill —
   *  it does not, ResolveFillAsync resolves one. Sending the prescription dropped the fill from
   *  the write, so the edit was filed against the newest fill (Rx 149012). */
  wbRxId: string | null
  /** Route for "Open full record" (left-click parity). */
  openHref: string | null
  onClose: () => void
  /** Deep link to a row that is not in the loaded list — degrade honestly. */
  notFound?: boolean
  /** Fired after a workbench edit inside the drawer SAVES — the owning grid updates its
   *  own row data (or invalidates its query) so the list reflects the change immediately,
   *  not on the next 15s poll. */
  onWbSaved?: (fieldKey: string, value: string | null) => void
}) {
  const navigate = useNavigate()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div role="presentation" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.25)' }}>
      <aside role="dialog" aria-label={`Prescription ${title}`} onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(440px, 92vw)',
          background: 'var(--color-surface, #fff)', borderLeft: '1px solid var(--color-border, #DED8CC)',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column',
        }}>
        <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--color-border, #DED8CC)' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12.5, color: 'var(--color-text-mute, #6b7280)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {openHref && (
              <button type="button" onClick={() => navigate(openHref)}
                title="Open the full prescription record"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid var(--color-border, #DED8CC)', background: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12.5 }}>
                <ExternalLink size={13} /> Full record
              </button>
            )}
            <button type="button" aria-label="Close panel" onClick={onClose}
              style={{ border: 'none', background: 'none', cursor: 'pointer', lineHeight: 0, padding: 6 }}>
              <X size={17} />
            </button>
          </div>
        </header>
        <div style={{ overflowY: 'auto', padding: '10px 16px 24px' }}>
          {notFound ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-mute, #6b7280)', padding: '18px 0' }}>
              This prescription is not in the currently loaded list (it may be filtered out or on
              an unloaded page).{openHref && <> Use <b>Full record</b> above to open it directly.</>}
            </div>
          ) : (<>
            {fields.map(f => (
              <div key={f.label} style={rowStyle}>
                <span style={labStyle}>{f.label}</span>
                <span style={{ minWidth: 0 }}>{f.node}</span>
              </div>
            ))}
            {wbFields.length > 0 && wbRxId && (
              <>
                <div style={{ margin: '16px 0 4px', fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-mute, #6b7280)' }}>
                  WorkBench — edits save immediately
                </div>
                {wbFields.map(f => (
                  <div key={f.key} style={rowStyle}>
                    <span style={labStyle}>{f.label}</span>
                    <span style={{ minWidth: 0 }}>
                      {f.key === 'task_due_date' ? (
                        // ASP-64: a DATE field with no workbench_field_defs entry (physical
                        // rx_workbench column, mig 223) — WorkBenchCell's no-def fallback would
                        // pill the raw ISO. Same editor + PUT as the grid cell.
                        <WorkBenchDateCell fieldKey="task_due_date" rxId={wbRxId} current={f.current}
                          onSaved={v => onWbSaved?.(f.key, v)} />
                      ) : (
                        <WorkBenchCell fieldDef={wbDefs[f.key]} rxId={wbRxId} current={f.current}
                          onSaved={v => onWbSaved?.(f.key, v)} />
                      )}
                    </span>
                  </div>
                ))}
              </>
            )}
          </>)}
        </div>
      </aside>
    </div>,
    document.body,
  )
}

/** One-line dismissible tip that teaches the right-click shortcut. */
export function RxDrawerHint() {
  const KEY = 'owlrx_rx_drawer_hint_dismissed'
  const [hidden, setHidden] = React.useState(() => {
    try { return localStorage.getItem(KEY) === '1' } catch { return false }
  })
  if (hidden) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--color-text-mute, #6b7280)', background: 'var(--color-surface-raised, #f8f7f4)', border: '1px solid var(--color-border, #DED8CC)', borderRadius: 7, padding: '6px 10px', margin: '6px 0' }}>
      <Lightbulb size={14} style={{ flexShrink: 0 }} />
      <span><b>Tip:</b> right-click any row to open it in a side panel — it stays put while the list refreshes.</span>
      <button type="button" aria-label="Dismiss tip"
        onClick={() => { setHidden(true); try { localStorage.setItem(KEY, '1') } catch { /* private mode */ } }}
        style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 0, padding: 2 }}>
        <X size={13} />
      </button>
    </div>
  )
}
